import 'intersection-observer'; // Polyfill
import Debouncer from "../lib/Debouncer";

type Edges = {
	top?: boolean,
	left?: boolean,
	bottom?: boolean,
	right?: boolean
}

type BaseAntTarget = {
	id: number,
	mandatory?: boolean,
	priorityMultiplier?: number,
	mostRecentSticks?: [],
	corners?: CornerName[],
	getRect: () => (Rectangle|undefined)
}
type DOMAntTarget = BaseAntTarget & {
	element: HTMLElement,
	edges?: Edges
};
type BaseAdditionalTargetProperties = BaseAntTarget & {
	selector: string,
	pseudoElement?: string,
	antDetonator?: boolean
}
type TopOrBottom = "top"|"bottom";
type LeftOrRight = "left"|"right";
type CornerName = `${TopOrBottom}-${LeftOrRight}`;
function isCornerName(possible: string): possible is CornerName {
	const split = possible.split('-');
	return split.length === 2 && (split[0] === "top" || split[0] === "bottom") && (split[1] === "left" || split[1] === "right");
}

type AdditionalTarget = BaseAdditionalTargetProperties & ({
	position: StickType.horizontal|StickType.vertical
}|{
	position: "corner",
	corners: CornerName[]
}|{
	position: "side",
	edges?: Edges
});
type RawAdditionalTarget = Omit<AdditionalTarget|BaseAdditionalTargetProperties, "id"|"edges"|"getRect"> & {
	position: "horizontal"|"vertical",
	sides?: (TopOrBottom|LeftOrRight)[]|undefined
};

type AntTarget = DOMAntTarget|AdditionalTarget;
function isDOMAntTarget(target: AntTarget|undefined): target is DOMAntTarget {
	return target !== undefined && "element" in target && target.element instanceof HTMLElement;
}

type Pair<L, R> = [L, R];

const enum StickType {
	horizontal,
	vertical,
	point
}

abstract class AbstractStick<T extends StickType, I extends AbstractStick<T, I>> {
	public readonly sourceId;

	public readonly scrollX;
	public readonly scrollY;

	protected constructor(
		public readonly source: AntTarget,
		public readonly sourceStickId: number,
		public readonly type: T,
		public readonly positionInStick: number|undefined,
		public readonly stickOffset: number
	) {
		this.sourceId = source.id;
		this.scrollX = window.scrollX;
		this.scrollY = window.scrollY;
	}

	public abstract normalize(positionInStick?: number): I;

	public abstract realign(): I;

	public abstract toPoint(): Point;
}
class PointStick extends AbstractStick<StickType.point, PointStick> {
	constructor(
		source: AntTarget, sourceStickId: number,
	    public readonly x: number,
		public readonly y: number,
		positionInStick = 0
	) {
		super(source, sourceStickId, StickType.point, positionInStick, 0);
	}

	normalize(positionInStick: number) {
		return positionInStick !== this.positionInStick ? new PointStick(this.source, this.sourceStickId, this.x, this.y, positionInStick) : this;
	}

	realign() {
		return new PointStick(this.source, this.sourceStickId, this.x - this.scrollX + window.scrollX, this.y - this.scrollY + window.scrollY, this.positionInStick);
	}

	toPoint(): Point {
		return [this.x, this.y];
	}
}
class HorizontalStick extends AbstractStick<StickType.horizontal, HorizontalStick> {
	constructor(
		source: AntTarget, sourceStickId: number,
		public readonly x: Pair<number, number>,
		public readonly y: number,
		stickOffset = 0,
		positionInStick?: number
	) {
		super(source, sourceStickId, StickType.horizontal, positionInStick, stickOffset);
	}

	normalize(positionInStick: number) {
		if (this.stickOffset === 0) {
			if (positionInStick !== this.positionInStick) {
				return new HorizontalStick(this.source, this.sourceStickId, this.x, this.y, 0, positionInStick);
			} else {
				return this;
			}
		} else {
			return new HorizontalStick(this.source, this.sourceStickId, [this.x[0] - this.stickOffset, this.x[1]], this.y,
				0, (positionInStick !== this.positionInStick ? positionInStick : this.positionInStick) + this.stickOffset);
		}
	}

	realign() {
		const deltaX = window.scrollX - this.scrollX;
		const deltaY = window.scrollY - this.scrollY;
		return new HorizontalStick(this.source, this.sourceStickId, [this.x[0] + deltaX, this.x[1] + deltaX], this.y + deltaY, this.stickOffset, this.positionInStick);
	}

	toPoint(): Point {
		if (this.positionInStick === undefined) {
			throw new Error("An un-normalized Stick was used in a point calculation");
		}
		return [this.x[0] + this.positionInStick - this.stickOffset, this.y];
	}
}
class VerticalStick extends AbstractStick<StickType.vertical, VerticalStick> {
	constructor(
		source: AntTarget, sourceStickId: number,
		public readonly x: number,
		public readonly y: Pair<number, number>,
		stickOffset = 0,
		positionInStick?: number
	) {
		super(source, sourceStickId, StickType.vertical, positionInStick, stickOffset);
	}

	normalize(positionInStick: number) {
		if (this.stickOffset === 0) {
			if (positionInStick !== this.positionInStick) {
				return new VerticalStick(this.source, this.sourceStickId, this.x, this.y, 0, positionInStick);
			} else {
				return this;
			}
		} else {
			return new VerticalStick(this.source, this.sourceStickId, this.x, [this.y[0] - this.stickOffset, this.y[1]],
				0, (positionInStick !== this.positionInStick ? positionInStick : this.positionInStick) + this.stickOffset);
		}
	}

	realign() {
		const deltaX = window.scrollX - this.scrollX;
		const deltaY = window.scrollY - this.scrollY;
		return new VerticalStick(this.source, this.sourceStickId, this.x + deltaX, [this.y[0] + deltaY, this.y[1] + deltaY], this.stickOffset, this.positionInStick);
	}

	toPoint(): Point {
		if (this.positionInStick === undefined) {
			throw new Error("An un-normalized Stick was used in a point calculation");
		}
		return [this.x, this.y[0] + this.positionInStick - this.stickOffset];
	}
}

type Stick = PointStick|HorizontalStick|VerticalStick;

type Rectangle = {
	top: number,
	left: number,
	bottom: number,
	right: number
};
function rectangleFromComputedStyle(owningElement: Element, computedStyle: ReturnType<Window["getComputedStyle"]>): Rectangle {
	const boundingClientRect = owningElement.getBoundingClientRect();
	const top = boundingClientRect.top + parseFloat(computedStyle.top), left = boundingClientRect.left + parseFloat(computedStyle.left);
	const bottom = parseFloat(computedStyle.bottom), right = parseFloat(computedStyle.right);
	return {top, left, bottom: bottom ? bottom : (top + parseFloat(computedStyle.height)), right: right ? right : (left + parseFloat(computedStyle.width))};
}

// 5% from either edge
function calculateAllowedBounds(): Rectangle {
	const fivePercentX = Math.floor(window.innerWidth / 20);
	const fivePercentY = Math.floor(window.innerHeight / 20);
	return {
		top: fivePercentY,
		left: fivePercentX,
		bottom: window.innerHeight - fivePercentY,
		right: window.innerWidth - fivePercentX
	};
}

const leftAndRightOnly: Edges = {top: false, left: true, bottom: false, right: true};
let globalAntTargetIndex = 0;
function makeAntTargetForElement(element: HTMLElement, sidesOnly: boolean, priorityMultiplier = 1): DOMAntTarget {
	const rawCorners = element.getAttribute("data-ant-target-corner");
	return {
		id: globalAntTargetIndex++,
		element,
		mandatory: element.classList.contains("antTarget--mandatory"),
		corners: rawCorners ? rawCorners.split(',').filter<CornerName>(isCornerName) : undefined,
		edges: sidesOnly ? leftAndRightOnly : undefined,
		priorityMultiplier,
		getRect: () => element.getBoundingClientRect()
	};
}

const MAXIMUM_MOVEMENT_DURATION = 5000 as const; // We cap the animation duration at 3 seconds
const MOVEMENT_MS_PER_PIXEL = 4 as const;
type Point = Pair<number, number>;

const DISTINCT_FRAMES = 6 as const;
const FPS = 3 * DISTINCT_FRAMES;
const MS_PER_ANT_FRAMES = 1000 / FPS;
const THIRTY_FPS = 1000 / 30;

type RegisteredEventListener<K, L, O> = [ { removeEventListener: (type: K, listener: L, options?: O) => void }, K, L, O ];

type BasicAnimation = {
	start: () => void,
	running: () => boolean,
	stop: () => void
}
function makeBasicAnimation(element: HTMLElement, frameIdentifier: string, msPerFrame: number, singleRun = false, onFrame: (frameNumber: number) => void = () => {}): BasicAnimation {
	const frames: HTMLElement[] = [];
	element.querySelectorAll<HTMLElement>(`.frame.frame--${frameIdentifier}`)
		.forEach(e => frames.push(e));

	let frameIndex = 0;
	let animating = false;
	let animationTimeout: number|undefined = undefined;
	let animationLoopFrameIdentifier: number|undefined = undefined;

	const animationLoop = () => {
		animationTimeout = undefined;
		if (animating) {
			animationLoopFrameIdentifier = window.requestAnimationFrame(() => {
				animationLoopFrameIdentifier = undefined;
				const oldFrame = frameIndex;
				if (singleRun && frameIndex + 1 === frames.length) {
					return;
				}
				frameIndex = (frameIndex + 1) % frames.length;
				onFrame(frameIndex);
				frames[frameIndex].classList.add("visible");
				frames[oldFrame].classList.remove("visible");

				if (animating) {
					animationTimeout = window.setTimeout(animationLoop, msPerFrame);
				}
			});
		}
	};

	return {
		start: () => {
			if (!animating) {
				animating = true;
				animationLoop();
			}
		},
		running: () => animating,
		stop: () => {
			if (animating) {
				animating = false;
				if (animationTimeout !== undefined) {
					clearTimeout(animationTimeout);
					animationTimeout = undefined;
				}
				if (animationLoopFrameIdentifier !== undefined) {
					window.cancelAnimationFrame(animationLoopFrameIdentifier);
					animationLoopFrameIdentifier = undefined;
				}
			}
		}
	}
}

type MovementVector = {
	readonly originPoint: Point;
	readonly scalarDistance: number;

	readonly duration: number;
	readonly pixelsFromMS: (deltaT: number) => number;

	readonly distanceX: number;
	readonly directionX: number;

	readonly distanceY: number;
	readonly directionY: number;

	readonly theta: number;
	readonly visualTheta: number;
}

class Ant {
	private readonly walkingAnimation: BasicAnimation;

	private readonly targets: AntTarget[] = [];
	private readonly registeredEventListeners: RegisteredEventListener<any, any, any>[] = [];
	private readonly originalPosition: Point;
	private readonly antGraphicDimensions: Pair<number, number>;
	private readonly antGraphicOffset: Pair<number, number>;

	private allowedBounds = calculateAllowedBounds();

	private readonly ctaIntersectionObserver?: IntersectionObserver;
	private ctaButtonVisible = false;

	private lastTarget?: AntTarget;
	private currentTarget?: AntTarget;

	private position: Point;
	private destination?: Stick;
	private movementVector?: MovementVector|null;

	private movementAnimationHookIdentifier?: number;
	private movementTimeoutIdentifier?: number;

	constructor(public readonly antElement: HTMLElement) {
		this.walkingAnimation = makeBasicAnimation(antElement, "walking", MS_PER_ANT_FRAMES);

		const antClientRect = this.antElement.getBoundingClientRect();
		this.antGraphicDimensions = [antClientRect.width, antClientRect.height];
		this.antGraphicOffset = [Math.floor(antClientRect.width / 2), Math.floor(antClientRect.height / 2)];

		const boundingClientRect = antElement.getBoundingClientRect();
		this.position = this.originalPosition = [boundingClientRect.left + window.scrollX, boundingClientRect.top + window.scrollY];

		document.querySelectorAll("main section").forEach(mainSection => {
			mainSection.querySelectorAll<HTMLElement>(":not(.antTarget--ignore) h1,h2,h3,h4,h5,h6,p").forEach(sidesOnlyElement => {
				// We know that parentElement exists because this querySelectorAll is running on an element instead of the Document
				if (sidesOnlyElement.parentElement?.classList.contains("wp-block-media-text__content")) {
					this.targets.push(makeAntTargetForElement(sidesOnlyElement.parentElement, true));
				} else {
					this.targets.push(makeAntTargetForElement(sidesOnlyElement, true));
				}
			});
			mainSection.querySelectorAll<HTMLElement>(":not(.antTarget--ignore) figure,.antTarget").forEach(element => {
				this.targets.push(makeAntTargetForElement(element, false, 5));
			});
		});

		const additionalTargetsAttr = antElement.getAttribute("data-additional-targets");
		if (additionalTargetsAttr) {
			this.targets.push(...(JSON.parse(additionalTargetsAttr) as RawAdditionalTarget[]).map(e => {
				let edges: Edges|undefined = undefined;
				if (e.sides) {
					edges = {top: e.sides.indexOf("top") !== -1, left: e.sides.indexOf("left") !== -1, bottom: e.sides.indexOf("bottom") !== -1, right: e.sides.indexOf("right") !== -1};
				}
				const res = {
					...e,
					id: globalAntTargetIndex++,
					edges,
					position: e.position === "horizontal" ? StickType.horizontal : (e.position === "vertical" ? StickType.vertical : e.position),
					getRect: () => {
						const querySelectorResult = document.querySelector(res.selector);
						return querySelectorResult ? (
							res.pseudoElement
								? rectangleFromComputedStyle(querySelectorResult, window.getComputedStyle(querySelectorResult, res.pseudoElement))
								: querySelectorResult.getBoundingClientRect()
						) : undefined;
					}
				} as AdditionalTarget;
				return res;
			}));
		}

		this.registerEventListener(window, 'resize', new Debouncer(() => {
			const potentiallyChangedAllowedBounds = calculateAllowedBounds();
			if (this.allowedBounds.top !== potentiallyChangedAllowedBounds.top
				|| this.allowedBounds.left !== potentiallyChangedAllowedBounds.left
				|| this.allowedBounds.bottom !== potentiallyChangedAllowedBounds.bottom
				|| this.allowedBounds.right !== potentiallyChangedAllowedBounds.right
			) {
				this.allowedBounds = potentiallyChangedAllowedBounds;
				this.updateDestination();
				this.beginMoving();
			}
		}).run);

		this.registerEventListener(document, 'scroll', new Debouncer(() => {
			this.updateDestination();
			this.beginMoving();
		}, false, 1000).run);

		const ctaButton = document.querySelector(".entryCTA .wp-block-button__link");
		if (ctaButton) {
			this.ctaIntersectionObserver = new IntersectionObserver(
				entries => {
					for (let entry of entries) {
						this.ctaButtonVisible = entry.isIntersecting;
					}
					if (this.ctaButtonVisible) {
						window.requestIdleCallback(() => this.detonateIfAppropriate());
					}
				}, {
					root: null,
					rootMargin: `0px`,
					threshold: 1.0
				});
			this.ctaIntersectionObserver.observe(ctaButton);
		}
	}

	private detonateIfAppropriate() {
		if (this.ctaButtonVisible && !this.walkingAnimation.running()) {
			const currentTarget = this.currentTarget;
			if (isDOMAntTarget(currentTarget)) {
				if (currentTarget.element.getAttribute("data-ant-detonator") === "true") {
					this.detonate();
					return true;
				}
			} else if (currentTarget) {
				if (currentTarget.antDetonator || document.querySelector(currentTarget.selector)?.getAttribute("data-ant-detonator") === "true") {
					this.detonate();
					return true;
				}
			}
		}
		return false;
	}

	private isInAllowedBounds = (rectOrPoint: Rectangle|PointStick|Point) => {
		if (Array.isArray(rectOrPoint)) {
			return rectOrPoint[0] >= this.allowedBounds.left && rectOrPoint[0] <= this.allowedBounds.right
				&& rectOrPoint[1] >= this.allowedBounds.top && rectOrPoint[1] <= this.allowedBounds.bottom;
		} else if ('x' in rectOrPoint) {
			return rectOrPoint.x >= this.allowedBounds.left && rectOrPoint.x <= this.allowedBounds.right
				&& rectOrPoint.y >= this.allowedBounds.top && rectOrPoint.y <= this.allowedBounds.bottom;
		}
		return (rectOrPoint.bottom >= this.allowedBounds.top && rectOrPoint.top <= this.allowedBounds.bottom)
			&& (rectOrPoint.right >= this.allowedBounds.left && rectOrPoint.left <= this.allowedBounds.right);
	};

	private convertTargetToVisibleSticks(target: AntTarget): Stick[] {
		const rect = target.getRect();
		if (rect === undefined || !this.isInAllowedBounds(rect)) {
			return [];
		}

		if (target.corners) {
			return target.corners.map<PointStick>(cornerName => {
				if (cornerName.charAt(cornerName.indexOf('-') + 1) === 'l') {
					if (cornerName.charAt(0) === 't') {
						return new PointStick(target, 0, rect.left, rect.top);
					} else {
						return new PointStick(target, 1, rect.left, rect.bottom);
					}
				} else {
					if (cornerName.charAt(0) === 't') {
						return new PointStick(target, 2, rect.right, rect.top);
					} else {
						return new PointStick(target, 3, rect.right, rect.bottom);
					}
				}
			}).filter(this.isInAllowedBounds);
		} else if (!("edges" in target) || !target.edges) {
			return [];
		}

		const validBounds: Rectangle = {
			top: Math.max(rect.top, this.allowedBounds.top),
			left: Math.max(rect.left, this.allowedBounds.left),
			bottom: Math.min(rect.bottom, this.allowedBounds.bottom),
			right: Math.min(rect.right, this.allowedBounds.right)
		};
		if (validBounds.top >= validBounds.bottom || validBounds.left >= validBounds.right) {
			return [];
		}
		const res: Stick[] = [];
		if ((!target.edges || target.edges.top) && validBounds.top === rect.top) {
			res.push(new HorizontalStick(target, 0, [validBounds.left, validBounds.right], rect.top, validBounds.left - rect.left));
		}
		if ((!target.edges || target.edges.bottom) && validBounds.bottom === rect.bottom) {
			res.push(new HorizontalStick(target, 1, [validBounds.left, validBounds.right], rect.bottom, validBounds.left - rect.left));
		}

		if ((!target.edges || target.edges.left) && validBounds.left === rect.left) {
			res.push(new VerticalStick(target, 2, rect.left, [validBounds.top, validBounds.bottom], validBounds.top - rect.top));
		}
		if ((!target.edges || target.edges.right) && validBounds.right === rect.right) {
			res.push(new VerticalStick(target, 3, rect.right, [validBounds.top, validBounds.bottom], validBounds.top - rect.top));
		}
		return res;
	}

	private updateDestination() {
		if (this.movementAnimationHookIdentifier !== undefined) {
			window.cancelAnimationFrame(this.movementAnimationHookIdentifier);
		}
		this.movementVector = undefined;

		const currentDestination = this.destination;
		if (currentDestination) {
			const stickInformation = this.convertTargetsToSticks();
			if (stickInformation[0].findIndex(stick => stick[1].sourceId === currentDestination.sourceId && stick[1].sourceStickId === currentDestination.sourceStickId) !== -1) {
				const realignedCurrentDestination = currentDestination.realign();
				if (this.isInAllowedBounds(realignedCurrentDestination.toPoint())) {
					this.destination = realignedCurrentDestination;
					return;
				}
			}
			this.destination = this.pickNextLocation(stickInformation);
		} else {
			this.destination = this.pickNextLocation();
		}
	}

	private beginMoving() {
		const movementVector = this.calculateMovementVector();
		if (movementVector !== null) {
			this.walkingAnimation.start();
			const initialTimestamp = performance.now();
			let lastTimestamp: number;

			const frameRequestCallback: FrameRequestCallback = timestamp => {
				if (this.movementVector !== movementVector) {
					return;
				}
				this.movementAnimationHookIdentifier = undefined;

				if (timestamp !== lastTimestamp) {
					lastTimestamp = timestamp;
					const deltaT = timestamp - initialTimestamp;
					this.translate(movementVector, deltaT);
					if (deltaT < movementVector.duration) {
						this.movementAnimationHookIdentifier = window.requestAnimationFrame(frameRequestCallback);
					} else {
						this.stopMoving(true);
					}
				}
			};
			this.movementAnimationHookIdentifier = window.requestAnimationFrame(frameRequestCallback);
		} else {
			this.walkingAnimation.stop();
		}
	}

	private stopMoving(stoppedMovingNaturally: boolean) {
		this.walkingAnimation.stop();
		this.movementVector = undefined;
		this.destination = undefined;
		if (this.movementAnimationHookIdentifier !== undefined) {
			window.cancelAnimationFrame(this.movementAnimationHookIdentifier);
		}
		if (!stoppedMovingNaturally || !this.detonateIfAppropriate()) {
			this.beginMovementTimeoutWait();
		}
	}

	// Only call this from within a FrameRequestCallback
	private translate(movementVector: MovementVector, deltaT: number) {
		if (movementVector) {
			let deltaX: number, deltaY: number;
			if (deltaT >= movementVector.duration) {
				deltaX = movementVector.distanceX;
				deltaY = movementVector.distanceY;
			} else {
				const currentPixels = movementVector.pixelsFromMS(deltaT);
				deltaX = currentPixels * Math.sin(movementVector.theta) * movementVector.directionX;
				deltaY = currentPixels * Math.cos(movementVector.theta) * movementVector.directionY;
			}
			this.position = [movementVector.originPoint[0] + deltaX, movementVector.originPoint[1] + deltaY];
			const translateX = this.position[0] - this.originalPosition[0];
			const translateY = this.position[1] - this.originalPosition[1];
			this.antElement.style.transform = `translate(${translateX - this.antGraphicOffset[0]}px, ${translateY - this.antGraphicOffset[1]}px) rotate(${movementVector.visualTheta}rad)`;
		}
	}

	private calculateMovementVector(): MovementVector|null {
		if (this.movementVector !== undefined) {
			return this.movementVector;
		}
		if (!this.destination) {
			return this.movementVector = null;
		}

		const minPosX = window.scrollX - this.antGraphicDimensions[0];
		const maxPosX = window.scrollX + window.innerWidth;

		const minPosY = window.scrollY - this.antGraphicDimensions[1];
		const maxPosY = window.scrollY + window.innerHeight;
		this.position = [Math.min(Math.max(this.position[0], minPosX), maxPosX), Math.min(Math.max(this.position[1], minPosY), maxPosY)];

		const destinationPoint = this.destination.toPoint();
		const distanceX = destinationPoint[0] + window.scrollX - this.position[0];
		const distanceY = destinationPoint[1] + window.scrollY - this.position[1];

		if (distanceX === 0 && distanceY === 0) {
			return this.movementVector = null;
		}

		const directionX = Math.sign(distanceX);
		const directionY = Math.sign(distanceY);

		const theta = Math.atan(distanceX / distanceY * directionX * directionY);
		let visualTheta: number;

		if (directionX === -1) {
			if (directionY === -1) {
				visualTheta = -theta;
			} else {
				visualTheta = theta - Math.PI;
			}
		} else {
			if (directionY === -1) {
				visualTheta = theta;
			} else {
				visualTheta = -(theta + Math.PI);
			}
		}

		const scalarDistance = Math.sqrt((distanceX ** 2) + (distanceY ** 2));
		const potentialDuration = MOVEMENT_MS_PER_PIXEL * scalarDistance;
		const duration = Math.min(potentialDuration, MAXIMUM_MOVEMENT_DURATION);
		// This will almost always equal deltaT / MOVEMENT_MS_PER_PIXEL, but will be larger in the event that the movement would have taken more than 3 seconds.
		const pixelsFromMS = potentialDuration <= MAXIMUM_MOVEMENT_DURATION
			? (deltaT: number) => deltaT / MOVEMENT_MS_PER_PIXEL // If the movement duration would already be <= the maximum movement duration, we can avoid an extra multiplication step
			: (deltaT: number) => (deltaT * scalarDistance) / MAXIMUM_MOVEMENT_DURATION; // If the movement duration would otherwise be > the allowed maximum movement duration, we have to do this calc

		return this.movementVector = { originPoint: this.position, scalarDistance, duration, pixelsFromMS, distanceX, directionX, distanceY, directionY, theta, visualTheta };
	}

	private registerEventListener<K, L, O>(
		emitter: {addEventListener: (key: K, listener: L, options?: O) => void, removeEventListener: (key: K, listener: L, options?: O) => void}, key: K, listener: L, options?: O
	) {
		emitter.addEventListener(key, listener, options);
		this.registeredEventListeners.push([emitter, key, listener, options]);
	}

	public beginMovementTimeoutWait() {
		if (this.movementTimeoutIdentifier !== undefined) {
			clearTimeout(this.movementTimeoutIdentifier);
		}
		this.movementTimeoutIdentifier = window.setTimeout(() => {
			this.moveToNextLocation();
		}, 15_000); // 15 seconds
	}

	public moveToNextLocation() {
		this.updateDestination();
		if (this.destination) {
			if (this.movementTimeoutIdentifier !== undefined) {
				clearTimeout(this.movementTimeoutIdentifier);
			}
			this.beginMoving();
		}
	}

	private pickNextLocation(stickInformation = this.convertTargetsToSticks()): Stick|undefined {
		const [sticks, totalStickLength] = stickInformation;
		if (totalStickLength === 0) {
			return undefined;
		}

		const position = Math.random() * totalStickLength;
		for (let i = 0; i < sticks.length; i++) {
			if (position <= sticks[i][0]) {
				const stick = sticks[i][1];
				const stickOffset = (i === 0 ? 0 : sticks[i - 1][0]);
				const res = this.destination = stick.normalize(position - stickOffset);
				this.lastTarget = this.currentTarget;
				this.currentTarget = stick.source;
				return res;
			}
		}
		throw new Error("This should never be reachable");
	}

	private convertTargetsToSticks(): Pair<[number, Stick][], number> {
		const sticks: [number, Stick][] = [];
		let totalStickLength = 0;
		const calculateStickLength = (visibleStick: Stick) =>
			visibleStick.type === StickType.horizontal
				? visibleStick.x[1] - visibleStick.x[0]
				: (visibleStick.type === StickType.vertical
					? visibleStick.y[1] - visibleStick.y[0]
					: 1);
		const incorporateVisibleSticks = (visibleSticks: Stick[]) => {
			for (const visibleStick of visibleSticks) {
				const stickLength = visibleStick.type === StickType.horizontal ? visibleStick.x[1] - visibleStick.x[0]
					: (visibleStick.type === StickType.vertical ? visibleStick.y[1] - visibleStick.y[0] : 1);

				const multiplier = visibleStick.source.priorityMultiplier ?? 1;
				for (let i = 0; i < multiplier; i++) {
					totalStickLength += stickLength;
					sticks.push([totalStickLength, visibleStick]);
				}
			}
		};

		for (const target of this.targets) {
			if (target.mandatory || (target !== this.lastTarget && target !== this.currentTarget)) {
				if (isDOMAntTarget(target) || target.position === "side" || target.position === "corner") {
					incorporateVisibleSticks(this.convertTargetToVisibleSticks(target));
				} else if (target.position === StickType.horizontal) {
					incorporateVisibleSticks(this.convertTargetToVisibleSticks({
						...target,
						position: "side",
						edges: {top: true, bottom: true}
					}));
				} else if (target.position === StickType.vertical) {
					incorporateVisibleSticks(this.convertTargetToVisibleSticks({
						...target,
						position: "side",
						edges: {left: true, right: true}
					}));
				}
			}
		}
		const mandatorySticks = sticks.filter(stick => stick[1].source.mandatory);
		if (mandatorySticks.length > 0) {
			const idealMandatorySticks = mandatorySticks.filter(stick => stick[1].source !== this.lastTarget && stick[1].source !== this.currentTarget);
			const properMandatorySticks = idealMandatorySticks.length > 0 ? idealMandatorySticks : mandatorySticks;
			return [properMandatorySticks, properMandatorySticks.reduce((a, b) => {
				const res = a + calculateStickLength(b[1]);
				b[0] = res;
				return res;
			}, 0)];
		}
		return [sticks, totalStickLength];
	}

	public detonate() {
		if (this.movementTimeoutIdentifier !== undefined) {
			clearTimeout(this.movementTimeoutIdentifier);
		}
		this.stopMoving(false);
		this.walkingAnimation.stop();
		this.ctaIntersectionObserver?.disconnect();
		this.registeredEventListeners.forEach(e => e[0].removeEventListener(e[1], e[2], e[3]));
		const detonator = () => {
			const explosion = this.antElement.querySelector<SVGElement>('.ant #explosion');
			if (explosion) {
				//TODO: Actually detonate

				//TODO: Wait for the detonation to complete
				explosion.addEventListener('transitionend', () => this.antElement.remove());
				explosion.addEventListener('animationend', () => this.antElement.remove());
				this.antElement.classList.add('exploding');
			} else {
				this.antElement.remove();
			}
		};

		const antSmasher = document.querySelector<HTMLElement>(".antSmasher");
		if (antSmasher) {
			makeBasicAnimation(antSmasher, "smashing", THIRTY_FPS, true, frameNumber => {
				if (frameNumber === 15) {
					detonator();
				}
			}).start();
		} else {
			detonator();
		}
	}
}

export function init() {
	if ('IntersectionObserver' in window) {
		document.querySelectorAll<HTMLElement>(".ant").forEach(antElement => new Ant(antElement));
	}
}
